import { useState } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { Dialog } from '../Dialog';
import { newIdempotencyKey } from '../../lib/idempotency';
import { useAppData } from '../../state/contexts/AppDataContext';
import { useUIState } from '../../state/contexts/UIStateContext';
import { ApiProblem } from '../../api/client';
import { createIntakeClient } from '../../api/intakeClients';

// #141 — provision a customer intake client (bcid-admin only). Two entry paths
// share the one-time-secret success view:
//   - Issue: one click for the company whose page the admin is on. The slug is
//     derived from the customer id and only surfaces when it has to; on success
//     the client id and secret are shown ONCE.
//   - Rotate: the page fires the rotate call itself and opens this modal with
//     modalData.rotatedSecret already set, so it lands straight on the success
//     view (no form).
// The secret is shown once and never stored (server never persists it).
//
// #323 — the customer id is NOT an input. This modal is only ever opened from a
// company's API Clients tab, so the company is already known; asking again let
// an admin sitting on Acme's page mint a client under a different tenant, and
// the tenant is baked into the client's protocol mapper permanently. It arrives
// as modalData.customerId, the same way CustomerLogins takes it as a prop.
//
// Nor is the client name. New customer ids are validated against this same
// grammar at creation (CUSTOMER_ID_PATTERN in routes/admin-customers.ts), so the
// name is always the customer id and there is nothing to decide. Letting it be
// edited only ever produced a client id that no longer named its company — the
// drift #323 set out to remove. Ids predating that rule are folded by
// deriveClientName; the rare one that cannot be folded is refused here with the
// break-glass procedure named, rather than papered over with a free-text field.

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,30}$/;

/**
 * The client name for a company. For any company created since the create-path
 * grammar was tightened this is the identity function; the folding below exists
 * only for ids backfilled by A011 out of pre-existing protocol mappers, which
 * may carry `.`, `_`, uppercase, or more than 31 characters. Returns '' when
 * nothing valid survives, which the caller treats as "refuse", not "guess".
 */
export function deriveClientName(customerId: string): string {
  const s = customerId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 31)
    .replace(/-+$/, '');
  return SLUG_RE.test(s) ? s : '';
}

// Read-once view of a client id + secret with copy buttons and the
// hand-over-out-of-band warning. Reused by create and rotate.
function OneTimeSecretView({ title, clientId, secret, note, onClose }: {
  title: string;
  clientId: string;
  secret: string;
  note: string;
  onClose: () => void;
}) {
  const { showToast } = useAppData();
  const [copied, setCopied] = useState<'id' | 'secret' | null>(null);
  const copy = (value: string, which: 'id' | 'secret') => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    }).catch(() => showToast('Copy failed — select and copy the value manually.'));
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <div style={{ fontSize: 13, color: C.text, marginBottom: 12 }}>
        {note} The secret is shown <strong>once</strong> — copy it now and hand both values to the
        customer over a secure channel (not by email).
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: C.textMuted }}>Client ID</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{ flex: 1, padding: '10px 12px', background: C.bgInput, border: `2px solid ${C.border}`, borderRadius: 8, fontSize: 14, wordBreak: 'break-all' }} data-testid="intake-client-id">{clientId}</code>
          <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => copy(clientId, 'id')}>{copied === 'id' ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 11, color: C.textMuted }}>Client secret</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{ flex: 1, padding: '10px 12px', background: C.bgInput, border: `2px solid ${C.border}`, borderRadius: 8, fontSize: 14, wordBreak: 'break-all' }} data-testid="intake-client-secret">{secret}</code>
          <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => copy(secret, 'secret')}>{copied === 'secret' ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.warn, marginBottom: 16 }}>⚠ This secret is not stored anywhere and cannot be retrieved again. If it is lost, rotate the secret to issue a new one.</div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.btn, ...styles.btnGold }} onClick={onClose}>Done</button>
      </div>
    </Dialog>
  );
}

export function CreateIntakeClientModal() {
  const { showToast } = useAppData();
  const { setModal, setModalData, modalData } = useUIState();

  // Fixed by the caller — the company whose page opened this. Never editable.
  const customerId = typeof modalData?.customerId === 'string' ? modalData.customerId : '';
  const customerName = typeof modalData?.customerName === 'string' ? modalData.customerName : customerId;

  const slug = deriveClientName(customerId);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ clientId: string; secret: string } | null>(null);

  const close = () => setModal(null);

  // Marks the list stale so the Customers page reloads on close.
  const markChanged = () => setModalData((d: Record<string, unknown>) => ({ ...d, intakeClientsChanged: Date.now() }));

  // Rotate path: the page opened us with a fresh secret already in hand.
  const rotated = modalData?.rotatedSecret as { clientId: string; secret: string } | undefined;
  if (rotated) {
    return (
      <OneTimeSecretView
        title="Secret rotated"
        clientId={rotated.clientId}
        secret={rotated.secret}
        note="The secret has been rotated; the previous secret stopped working immediately."
        onClose={close}
      />
    );
  }

  if (created) {
    return (
      <OneTimeSecretView
        title="API credentials issued"
        clientId={created.clientId}
        secret={created.secret}
        note="The API credentials for this company were issued."
        onClose={close}
      />
    );
  }

  // Should be unreachable: every caller opens this from a company. If it ever
  // is reached, refuse rather than fall back to a free-text tenant field.
  if (!customerId) {
    return (
      <Dialog title="Issue API Credentials" onClose={close}>
        <div style={{ fontSize: 13, color: C.danger, marginBottom: 16 }} role="alert">
          No company selected. Open this from a company&apos;s API Clients tab so the client is bound to the
          right tenant.
        </div>
        <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={close}>Close</button>
      </Dialog>
    );
  }

  // Only reachable for a company whose id predates the create-path grammar and
  // folds to nothing valid. There is no field to offer instead — the client id
  // has to name the company — so say what to do rather than invite a guess.
  if (!slug) {
    return (
      <Dialog title="Issue API Credentials" onClose={close}>
        <div style={{ fontSize: 13, color: C.danger, marginBottom: 16 }} role="alert">
          <code>{customerId}</code> cannot be turned into a client name (lowercase letters, numbers, and
          hyphens; 2&ndash;31 characters). This company predates the current customer-ID rule. Its credentials
          have to be provisioned with the break-glass <code>kcadm</code> procedure in the intake-client
          provisioning runbook.
        </div>
        <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={close}>Close</button>
      </Dialog>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await createIntakeClient({ slug, customerId }, newIdempotencyKey());
      markChanged();
      setCreated({ clientId: result.clientId, secret: result.clientSecret });
    } catch (err) {
      if (err instanceof ApiProblem) showToast(err.detail ? `${err.title}: ${err.detail}` : err.title);
      else showToast(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog title="Issue API Credentials" onClose={close}>
      <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 16 }}>
        Provisions the confidential Keycloak client this company authenticates with against the Onboarding
        Intake API. The client id and secret are shown once on success — start this when you are ready to hand
        them over.
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: C.textMuted }}>Company</div>
        <div style={{ fontSize: 14, color: C.text, marginTop: 4 }} data-testid="intake-client-customer">
          {customerName} <code style={{ color: C.textSecondary }}>{customerId}</code>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
          The client is bound to this company permanently. To issue credentials for a different company, open
          that company and issue them there.
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: C.textMuted }}>Client ID</div>
        <div style={{ fontSize: 14, color: C.text, marginTop: 4 }} data-testid="intake-client-preview">
          <code>bcid-intake-{slug}</code>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
          Follows the customer id, so the credential always names the company it belongs to.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.btn, ...styles.btnGold }} disabled={submitting} onClick={submit}>{submitting ? 'Issuing…' : 'Issue Credentials'}</button>
        <button style={{ ...styles.btn, ...styles.btnOutline }} disabled={submitting} onClick={close}>Cancel</button>
      </div>
    </Dialog>
  );
}
